/**
 * Vue composable for lazy loading images using IntersectionObserver.
 * Only loads images when they enter the viewport.
 *
 * This serves as a polyfill for situations where the native `loading="lazy"` attribute
 * needs additional control, such as in carousels or dynamic content where images should
 * only load when they become visible in the viewport.
 *
 * Usage:
 * ```vue
 * <template>
 *   <img ref="imageRef" />
 * </template>
 *
 * <script setup>
 * const imageRef = useTemplateRef<HTMLImageElement>('imageRef');
 * const { lazilyLoadImage } = useLazyLoadImage(0.1, 'https://example.com/image.jpg');
 *
 * onMounted(() => {
 *   lazilyLoadImage(imageRef.value);
 * });
 * </script>
 * ```
 *
 * @param threshold - A number between 0 and 1 indicating the percentage of the target element
 *                   that needs to be visible before triggering the load. Defaults to 0.1 (10%)
 * @param imageOriginalUrl - The URL of the image to be loaded when the element enters the viewport
 *
 * @returns An object containing:
 *          - lazilyLoadImage: Function that takes an HTMLImageElement and starts observing it for lazy loading
 *
 * @throws {Error} If threshold is not between 0 and 1
 * @throws {Error} If imageOriginalUrl is empty or invalid
 *
 * @note This composable only works on the client side and will return a no-op function on the server
 */
export function useLazyLoadImage(threshold = 0.1, imageOriginalUrl: string) {
	if (!import.meta.client) return { lazilyLoadImage: () => {} };

	if (threshold < 0 || threshold > 1) {
		throw new Error('Threshold must be between 0 and 1');
	}

	if (!imageOriginalUrl?.trim()) {
		throw new Error('Image URL is required');
	}

	const observedElements = new Set<HTMLImageElement>();

	const lazyObserver = new IntersectionObserver(
		(entries, observer) => {
			entries.forEach((entry: IntersectionObserverEntry) => {
				if (!entry.isIntersecting) return;

				const imgElement = entry.target as HTMLImageElement;

				const handleError = () => {
					console.error(`Failed to load image: ${imageOriginalUrl}`);
					imgElement.removeEventListener('error', handleError);
				};

				const cleanup = () => {
					observer.unobserve(imgElement);
					observedElements.delete(imgElement);
					imgElement.removeEventListener('load', cleanup);
					imgElement.removeEventListener('error', handleError);
				};

				imgElement.addEventListener('load', cleanup);
				imgElement.addEventListener('error', handleError);

				imgElement.src = imageOriginalUrl;
			});
		},
		{
			threshold,
			rootMargin: '50px'
		}
	);

	const lazilyLoadImage = (ref: HTMLImageElement | null) => {
		if (!ref) return;

		if (observedElements.has(ref)) return;

		observedElements.add(ref);
		lazyObserver.observe(ref);
	};

	onBeforeUnmount(() => {
		observedElements.forEach(element => {
			lazyObserver.unobserve(element);
		});
		observedElements.clear();
		lazyObserver.disconnect();
	});

	return {
		lazilyLoadImage
	};
}
